<script setup>

import NavButton from "@/components/NavButton.vue";
import LinkButton from "@/components/LinkButton.vue";
import PackageChoice from "@/components/subscriptions/PackageChoice.vue";
import { computed, onMounted, ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import SubService, { ACCESS_FREE, ACCESS_PRO } from '@/services/SubService.js'
import AppService from '@/services/AppService.js'
import { cloneDeep } from 'lodash'
import { useI18n } from 'vue-i18n'
import PrivacyPolicyModal from '@/modules/settings/PrivacyPolicyModal.vue'
import SuccessSheet from '@/components/subscriptions/SuccessSheet.vue'
import TermsOfUseModal from '@/modules/settings/TermsModal.vue'

const { t } = useI18n()

const emit = defineEmits([
  'upgrade'
])

const props = defineProps({
  offline: {
    type: Boolean,
    default: false
  }
})

const loading = ref(false)
const upgrading = ref(false)
const product = ref('pro_yearly')
const packages = ref([])
const featured = ref({
  product: null,
  save: null
})

const successSheetRef = ref(null)
const privacyPolicyModalRef = ref(null)

const platform = Capacitor.getPlatform();

const UNITS = {
  ANNUAL: 12,
  SIX_MONTH: 6,
  THREE_MONTH: 3,
  TWO_MONTH: 2,
  MONTHLY: 1,
  WEEKLY: 0.25,
  LIFETIME: 0
}

const isAndroid = computed(() => {
  return platform === 'android'
})

const plan = computed(() => {
  return SubService.state.plan
})

const isIOS = computed(() => {
  return platform === 'ios'
})

const isFree = computed(() => {
  return plan.value === ACCESS_FREE
})

const isPro = computed(() => {
  return plan.value === ACCESS_PRO
})

const packagesClasses = computed(() => {
  return {
    'grid gap-2 xs3:gap-3': packages.value.length > 1,
    'grid-cols-2': packages.value.length === 2,
    'grid-cols-3': packages.value.length > 2
  }
})

const canUpgrade = computed(() => {
  return Capacitor.isNativePlatform() &&
    isFree.value &&
    packages.value.length > 0
})

async function refreshPackages () {
  try {
    loading.value = true
    const originalPackages = await SubService.getAvailablePackages()
    const ps = cloneDeep(originalPackages)

    // Set featured package dynamically
    if (ps.length > 0) {
      const prices = ps
        // Remove lifetime or free products
        .filter((p) => UNITS[p.packageType] && UNITS[p.packageType] > 0)
        .map((p) => {
          const units = UNITS[p.packageType]
          const price = p.product.price

          return {
            unitPrice: Number((price / units).toFixed(2)),
            package: p
          }
        })
        .sort((a, b) => a.unitPrice - b.unitPrice)

      if (prices && prices.length > 0) {
        const maxPrice = prices[prices.length - 1].unitPrice
        const minPrice = prices[0].unitPrice

        const savePercent = Math.floor((maxPrice - minPrice) / maxPrice * 100)

        featured.value.product = prices[0].package.product.identifier
        featured.value.save = t('upgrade.ribbon.save', { percent: savePercent })
      }

      // Set featured/cheapest as preselected if default (pro_yearly) or previously selected doesn't exist
      if (!ps.some(p => p.product.identifier === product.value) && featured.value.product) {
        product.value = featured.value.product
      }
    }

    packages.value = ps
  } finally {
    loading.value = false
  }
}

function setProduct(identifier) {
  product.value = identifier
  AppService.registerAppEvent('upgrade_product_selected', identifier, { product: identifier })
}

async function upgrade () {
  if (upgrading.value) return

  try {
    upgrading.value = true
    AppService.registerAppEvent('upgrade_start', product.value, { product: product.value })
    await SubService.purchase(product.value)
    upgrading.value = false
    AppService.registerAppEvent('upgrade_completed', product.value, { product: product.value })
    await successSheetRef.value.open()// resolves on hide

    emit('upgrade', product.value)
  } catch (e) {
    AppService.registerAppEvent('upgrade_failed', this.product, { product: this.product, error: e })
  } finally {
    upgrading.value = false
  }
}

function reset() {
  upgrading.value = false
}

defineExpose({
  refreshPackages,
  reset
})


onMounted(() => refreshPackages())
</script>

<template>
    <div class="text-center">
        <div class="relative" v-if="loading">
            <f-loading :showing="loading" color="#FFFFFF"/>
        </div>

        <div :class="packagesClasses" v-if="packages.length > 0">
            <package-choice
                    v-for="p in packages"
                    :key="p.product.identifier"
                    :product="p"
                    :model-value="product"
                    @update:model-value="setProduct"
                    :show-ribbon="featured.product === p.product.identifier"
                    :ribbon="featured.save"
                    :horizontal-layout="false"
            />
        </div>

        <template v-if="isPro">
            <div class="bg-slate-blue-800 p-4 rounded text-white mt-4 xs:mt-8">{{ $t('upgrade.already.pro') }}</div>
        </template>

        <template v-else-if="offline">
            <div class="bg-primary/20 border border-primary p-4 rounded text-white mt-4 xs:mt-8">{{ $t('upgrade.offline') }}</div>
        </template>

        <template v-else-if="!loading">
            <template v-if="canUpgrade">
                <nav-button
                        :label="upgrading ? $t('upgrade.now.button.busy') : $t('upgrade.now.button')"
                        @click="upgrade()"
                        class="mt-6"
                        hide-icon
                        :busy="upgrading"
                        :disabled="upgrading"
                />

                <div v-if="isAndroid" class="text-xs text-slate-blue-100 mt-2">{{ $t('upgrade.google.play.disclaimer') }}</div>
                <div v-if="isIOS" class="text-xs text-slate-blue-100 mt-2">{{ $t('upgrade.appstore.disclaimer') }}</div>
            </template>

            <template v-else>
                <div class="bg-primary/20 border border-primary p-4 rounded text-white mt-4 xs:mt-8">{{ $t('upgrade.unavailable') }}</div>
            </template>
        </template>

        <div class="mt-2">
            <link-button class="text-xs text-slate-blue-100" @click="$refs.privacyPolicyModalRef.open()">{{ $t('privacy.policy') }}</link-button>
            <link-button class="ltr:ml-12 rtl:mr-12 text-xs text-slate-blue-100" @click="$refs.termsModalRef.open()">{{ $t('terms.of.use') }}</link-button>
        </div>

        <privacy-policy-modal ref="privacyPolicyModalRef"/>
        <terms-of-use-modal ref="termsModalRef"/>
        <success-sheet ref="successSheetRef"/>
    </div>
</template>

<style scoped lang="scss">

</style>
